import React, { useState, useEffect } from 'react';
import { suppliersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Search, Pencil, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  name: '', nickname: '', address: '', city: '', cap: '', piva: '', email: '', phone: '', note: '',
};

const SuppliersPage = () => {
  const { user } = useAuth();
  const isSlave = user?.role === 'company_user';

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async (q = '') => {
    try {
      const r = await suppliersApi.getAll(q);
      setSuppliers(r.data);
    } catch (e) { toast.error('Errore caricamento fornitori'); }
    finally { setLoading(false); }
  };

  const onSearch = (e) => { setSearch(e.target.value); fetchData(e.target.value); };

  const openCreate = () => { setSelected(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (s) => { setSelected(s); setForm({ ...emptyForm, ...s }); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error('Nome fornitore obbligatorio'); return; }
    setSaving(true);
    try {
      if (selected) {
        await suppliersApi.update(selected.id, form);
        toast.success('Fornitore aggiornato');
      } else {
        await suppliersApi.create(form);
        toast.success('Fornitore creato');
      }
      setDialogOpen(false);
      fetchData(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await suppliersApi.delete(selected.id);
      toast.success('Fornitore eliminato');
      setDeleteDialogOpen(false);
      fetchData(search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore eliminazione'); }
  };

  return (
    <div data-testid="suppliers-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Fornitori</h1>
          <p className="text-muted-foreground text-sm">Anagrafica fornitori per i DDT di carico magazzino</p>
        </div>
        <Button data-testid="new-supplier-btn" onClick={openCreate} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> NUOVO FORNITORE
        </Button>
      </div>

      <Card className="card-swiss">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="search-supplier-input" placeholder="Cerca per nome, P.IVA..." value={search} onChange={onSearch} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-muted-foreground">Caricamento...</div> :
           suppliers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun fornitore</p>
              <Button onClick={openCreate} className="mt-4 btn-primary">
                <Plus className="w-4 h-4 mr-2" /> CREA IL PRIMO FORNITORE
              </Button>
            </div>
           ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-swiss">Nome</TableHead>
                  <TableHead className="table-header-swiss">Città</TableHead>
                  <TableHead className="table-header-swiss">P.IVA</TableHead>
                  <TableHead className="table-header-swiss">Telefono</TableHead>
                  <TableHead className="table-header-swiss w-[120px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.id} className="table-row-swiss">
                    <TableCell className="table-cell-swiss">
                      <div>
                        <div className="font-medium">{s.name}</div>
                        {s.nickname && s.nickname !== s.name && <div className="text-xs text-muted-foreground italic">{s.nickname}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="table-cell-swiss">{s.city || '-'}</TableCell>
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] text-xs">{s.piva || '-'}</TableCell>
                    <TableCell className="table-cell-swiss">{s.phone || '-'}</TableCell>
                    <TableCell className="table-cell-swiss">
                      <div className="flex gap-1">
                        <Button data-testid={`edit-supplier-${s.id}`} variant="ghost" size="icon" onClick={() => openEdit(s)} className="h-8 w-8" title="Modifica">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!isSlave && (
                          <Button data-testid={`delete-supplier-${s.id}`} variant="ghost" size="icon" onClick={() => { setSelected(s); setDeleteDialogOpen(true); }} className="h-8 w-8 text-destructive" title="Elimina">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
           )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">{selected ? 'Modifica Fornitore' : 'Nuovo Fornitore'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ragione Sociale *</Label>
                <Input data-testid="supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome comune (nickname)</Label>
                <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} placeholder="Es. Casa del Caffè" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">P.IVA</Label>
                <Input data-testid="supplier-piva" value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Indirizzo</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Città</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">CAP</Label>
                <Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Annotazioni interne" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button data-testid="save-supplier-btn" type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Salvataggio...' : (selected ? 'Aggiorna' : 'Crea')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina fornitore</AlertDialogTitle>
            <AlertDialogDescription>
              Eliminare <b>{selected?.name}</b>? L'operazione fallirà se ci sono DDT acquisto collegati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-supplier-btn" onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuppliersPage;
